import { FormField, Input } from '../../../components/ui/FormField.js';
import { rm } from '../../penalties/penalties.js';
import { LastChanged } from '../parts/LastChanged.js';
import { SettingCard } from '../parts/parts.js';
import type { MileagePackages } from '../settingsTypes.js';
import type { CardProps } from './cardProps.js';

/**
 * 417 (#1464, US1, mock-up frame 1): the mileage packages offered on rentals under 24 hours of a car on Cartrack at a
 * pickup location with an area. Prices are typed in ringgit and saved in sen; the km in whole km. An empty field is
 * sent as nothing and a finer amount as typed, so the platform refuses them with its message instead of the page
 * saving 0 or rounding.
 */
type Terms = { [K in keyof MileagePackages]: number | null };
const DEFAULTS: Terms = {
  package1_price_minor: 0,
  package2_price_minor: 5000,
  package2_allowance_km: 200,
  rate_minor_per_km: 40,
};

function toSen(typed: string): number | null {
  if (typed.trim() === '') return null;
  const raw = Number(typed) * 100;
  const rounded = Math.round(raw);
  return Math.abs(raw - rounded) < 1e-6 ? rounded : raw;
}
const toKm = (typed: string) => (typed.trim() === '' ? null : Number(typed));
const ringgit = (sen: number | null) =>
  sen === null ? '' : Number.isInteger(sen) ? (sen / 100).toFixed(2) : String(sen / 100);
const whole = (v: number | null): v is number => v !== null && Number.isInteger(v) && v >= 0;

export function MileagePackagesCard({ draft, latest, refusal }: CardProps) {
  const value: Terms = draft.value('mileage_packages') ?? DEFAULTS;
  const set = (patch: Partial<Terms>) => draft.set('mileage_packages', { ...value, ...patch });
  const complete =
    whole(value.package1_price_minor) &&
    whole(value.package2_price_minor) &&
    whole(value.package2_allowance_km) &&
    whole(value.rate_minor_per_km);
  const example = complete
    ? `Package 2: ${rm(value.package2_price_minor as number)} for ${value.package2_allowance_km} km. Every km outside the area (Package 1) or over ${value.package2_allowance_km} km (Package 2): ${rm(value.rate_minor_per_km as number)}.`
    : 'Enter every price and the km in whole numbers to see the example.';

  return (
    <SettingCard
      testId="mileage-packages-card"
      title="Mileage packages"
      dirty={draft.isDirty('mileage_packages')}
      refusal={refusal('mileage_packages')}
      description={
        <p>
          For rentals under 24 hours of a Cartrack car at a pickup location with a zone. Existing
          bookings keep their own terms.
        </p>
      }
      footer={<LastChanged setting="mileage_packages" latest={latest} />}
    >
      <div className="flex flex-col gap-6">
        <section className="flex flex-col gap-3">
          <h3 className="text-sm font-semibold text-ink">Package 1 · Stay in the area</h3>
          <FormField
            label="Package 1 price (RM)"
            name="mileage_package1_price"
            hint="Usually free: the customer stays inside the area."
          >
            <Input
              id="mileage_package1_price"
              type="number"
              min={0}
              step="0.01"
              value={ringgit(value.package1_price_minor)}
              onChange={(e) => set({ package1_price_minor: toSen(e.target.value) })}
            />
          </FormField>
        </section>
        <section className="flex flex-col gap-3 border-t border-hairline pt-6">
          <h3 className="text-sm font-semibold text-ink">Package 2 · Go anywhere</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label="Package 2 price (RM)" name="mileage_package2_price">
              <Input
                id="mileage_package2_price"
                type="number"
                min={0}
                step="0.01"
                value={ringgit(value.package2_price_minor)}
                onChange={(e) => set({ package2_price_minor: toSen(e.target.value) })}
              />
            </FormField>
            <FormField label="Km included in Package 2" name="mileage_package2_km">
              <Input
                id="mileage_package2_km"
                type="number"
                min={1}
                step="1"
                value={
                  value.package2_allowance_km === null ? '' : String(value.package2_allowance_km)
                }
                onChange={(e) => set({ package2_allowance_km: toKm(e.target.value) })}
              />
            </FormField>
          </div>
        </section>
        <section className="flex flex-col gap-3 border-t border-hairline pt-6">
          <h3 className="text-sm font-semibold text-ink">Per-km rate</h3>
          <FormField
            label="Rate per extra km (RM)"
            name="mileage_rate_per_km"
            hint="Charged for km outside the area (Package 1) or over the km included (Package 2), after staff confirm."
          >
            <Input
              id="mileage_rate_per_km"
              type="number"
              min={0}
              step="0.01"
              value={ringgit(value.rate_minor_per_km)}
              onChange={(e) => set({ rate_minor_per_km: toSen(e.target.value) })}
            />
          </FormField>
          <p data-testid="mileage-packages-example" className="text-xs text-steel">
            {example}
          </p>
        </section>
      </div>
    </SettingCard>
  );
}
